import { useLayoutEffect } from 'react';
import { useBusinessContext } from '../../context/BusinessContext';
import { getBusinessFromLocalStorage } from '../../util/local-storage-service';
import { darkenHex } from '../../util/colorMath';

/**
 * Headless component — watches the selected business's three brand colors
 * and projects them onto the global design-system CSS variables so that
 * every consumer (buttons, sidebar accent, hover states, gradients, focus
 * rings) re-themes automatically.
 *
 * Resets to the defaults defined in `design-system.css` when no business
 * is selected or when the business has no brand colors set.
 */
const DEFAULTS = {
    primary: '#7266E8',
    primaryDark: '#6B53D4',
    primarySoft: '#EBE7FF',
    primarySofter: '#F6F3FF',
    primaryPill: '#EFEAFF',
    primaryDarker: '#2A2245',
    gradientPrimary: 'linear-gradient(95deg, #7266E8, #B566D4)',
    gradientPrimaryHover: 'linear-gradient(95deg, #6057CC, #A14FC0)',
    shadowPill: '0 4px 14px rgba(114, 102, 232, 0.3)',
    shadowPillHover: '0 6px 18px rgba(114, 102, 232, 0.38)',
    ringFocus: '0 0 0 3px rgba(114, 102, 232, 0.18)',
};

const hexToRgb = (hex: string): string => {
    const s = hex.startsWith('#') ? hex.slice(1) : hex;
    if (s.length !== 6) return '114, 102, 232';
    return `${parseInt(s.slice(0, 2), 16)}, ${parseInt(s.slice(2, 4), 16)}, ${parseInt(
        s.slice(4, 6),
        16
    )}`;
};

const BrandThemeApplier = () => {
    const { selectedBusiness } = useBusinessContext();

    // useLayoutEffect runs BEFORE the browser paints, so the brand vars are in
    // place for the first frame — no flash of the default theme. We also fall
    // back to the localStorage-cached business so a reload / return-from-login
    // is themed immediately, before the fresh business arrives from the API.
    useLayoutEffect(() => {
        const root = document.documentElement;
        const effective = selectedBusiness ?? getBusinessFromLocalStorage();
        const main = effective?.brandColor;

        // No brand color → reset to defaults (so re-renders / sign-out are clean).
        if (!main) {
            root.style.removeProperty('--gs-primary');
            root.style.removeProperty('--gs-primary-dark');
            root.style.removeProperty('--gs-primary-soft');
            root.style.removeProperty('--gs-primary-softer');
            root.style.removeProperty('--gs-primary-pill');
            root.style.removeProperty('--gs-gradient-primary');
            root.style.removeProperty('--gs-gradient-primary-hover');
            root.style.removeProperty('--gs-shadow-pill');
            root.style.removeProperty('--gs-shadow-pill-hover');
            root.style.removeProperty('--gs-ring-focus');
            return;
        }

        const strong =
            effective?.brandColorStrong || darkenHex(main);

        // The soft / softer / pill tints are brand-tinted SURFACE backgrounds
        // (hover, selected rows, chips, pills). Derive them by mixing the brand
        // into the themed surface token rather than into white — `var(--gs-surface)`
        // flips under [data-theme="dark"], so these tints become light in light
        // mode and dark in dark mode automatically (they recompute on theme
        // toggle since they reference the surface variable).
        const soft = `color-mix(in srgb, ${main} 18%, var(--gs-surface, #ffffff))`;
        const softer = `color-mix(in srgb, ${main} 10%, var(--gs-surface, #ffffff))`;
        const pill = `color-mix(in srgb, ${main} 14%, var(--gs-surface, #ffffff))`;

        const mainRgb = hexToRgb(main);

        root.style.setProperty('--gs-primary', main);
        root.style.setProperty('--gs-primary-dark', strong);
        root.style.setProperty('--gs-primary-soft', soft);
        root.style.setProperty('--gs-primary-softer', softer);
        root.style.setProperty('--gs-primary-pill', pill);
        root.style.setProperty(
            '--gs-gradient-primary',
            `linear-gradient(95deg, ${main}, ${strong})`
        );
        root.style.setProperty(
            '--gs-gradient-primary-hover',
            `linear-gradient(95deg, ${strong}, ${darkenHex(strong, 0.12)})`
        );
        root.style.setProperty(
            '--gs-shadow-pill',
            `0 4px 14px rgba(${mainRgb}, 0.3)`
        );
        root.style.setProperty(
            '--gs-shadow-pill-hover',
            `0 6px 18px rgba(${mainRgb}, 0.38)`
        );
        root.style.setProperty(
            '--gs-ring-focus',
            `0 0 0 3px rgba(${mainRgb}, 0.18)`
        );
    }, [
        selectedBusiness,
        selectedBusiness?.brandColor,
        selectedBusiness?.brandColorStrong,
        selectedBusiness?.brandColorSoft,
    ]);

    // Reference DEFAULTS so it's not pruned — useful if we ever need to
    // explicitly reset rather than removeProperty.
    void DEFAULTS;

    return null;
};

export default BrandThemeApplier;
